import React, { useMemo } from "react";
import Dialog from "../../../components/ui/Dialog";
import HierarchyChart from "../../../components/ui/HierarchyChart";
import {
  useBranchTree,
  useRoleTree,
} from "../../../api/queries/workflowQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import usePermissions from "../../../hooks/usePermissions";
import { getApiErrorMessage } from "../../../utils/apiError";
import "../../admin/admin.css";

const EMPTY = [];
const Loading = () => (
  <div className="text-center py-4">
    <span
      className="spinner-border text-primary"
      role="status"
      aria-label="Loading"
    ></span>
  </div>
);
const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

/** Manager avatar + name, or "No manager" (amber). */
export const ManagerLine = ({ manager }) =>
  manager ? (
    <span
      className="hc-person"
      title={
        manager.employeeNumber
          ? `Manager · ${manager.employeeNumber}`
          : "Manager"
      }
    >
      <span className="hc-avatar" aria-hidden="true">
        {initials(manager.name)}
      </span>
      {manager.name}
    </span>
  ) : (
    <span className="hc-person hc-warn">
      <i className="bi bi-exclamation-circle"></i>No manager
    </span>
  );

/**
 * Branch org chart: manager, headcount (own / incl. sub-branches).
 * onOpen(node): click a card; renderActions(node): buttons on each card / row
 * (stop propagation inside).
 */
export const BranchChart = ({ companyId, onOpen, renderActions, view }) => {
  const user = useCurrentUser();
  const { managedBranchIds } = usePermissions();
  const query = useBranchTree(companyId);
  const nodes = query.data?.data || EMPTY;
  // Managed branches and everything below them
  const { managed, under } = useMemo(() => {
    const m = new Set(managedBranchIds);
    const u = new Set();
    const walk = (list, inside) =>
      list.forEach((n) => {
        if (inside) u.add(n.id);
        walk(n.children || EMPTY, inside || m.has(n.id));
      });
    walk(nodes, false);
    return { managed: m, under: u };
  }, [nodes, managedBranchIds]);
  const card = (n) => (
    <>
      <div className="hc-card-title">{n.branchName}</div>
      <div className="hc-card-sub">
        {[n.branchCode, n.region].filter(Boolean).join(" · ") || " "}
      </div>
      <ManagerLine manager={n.manager} />
      <div
        className="hc-card-sub"
        title="Employees: own / including sub-branches"
      >
        <i className="bi bi-people me-1"></i>
        {n.employeeCount ?? 0} / {n.totalEmployeeCount ?? 0}
      </div>
      {(n.isActive === false ||
        n.id === user.branchId ||
        managed.has(n.id) ||
        under.has(n.id)) && (
        <div className="hc-card-badges">
          {n.isActive === false && (
            <span className="status-pill status-pill--gray">Inactive</span>
          )}
          {managed.has(n.id) ? (
            <span className="chip chip--sm">
              <i className="bi bi-star-fill me-1"></i>You manage
            </span>
          ) : n.id === user.branchId ? (
            <span className="chip chip--sm">Your branch</span>
          ) : (
            under.has(n.id) && (
              <span className="chip chip--sm chip--alt">Under you</span>
            )
          )}
        </div>
      )}
      {renderActions && (
        <div className="hc-card-actions">{renderActions(n)}</div>
      )}
    </>
  );
  const row = (n) => (
    <>
      <span className="org-name">{n.branchName}</span>
      {n.branchCode && <span className="org-meta">{n.branchCode}</span>}
      {n.isActive === false && (
        <span className="status-pill status-pill--gray">Inactive</span>
      )}
      {managed.has(n.id) && (
        <span className="chip chip--sm">
          <i className="bi bi-star-fill me-1"></i>You manage
        </span>
      )}
      {n.manager ? (
        <span className="chip chip--sm">
          <i className="bi bi-person-badge me-1"></i>
          {n.manager.name}
        </span>
      ) : (
        <span className="chip chip--sm chip--alt hc-warn">No manager</span>
      )}
      <span className="org-meta ms-auto">
        <i className="bi bi-people me-1"></i>
        {n.employeeCount ?? 0} / {n.totalEmployeeCount ?? 0}
      </span>
      {renderActions?.(n)}
    </>
  );

  return (
    <>
      {query.isLoading && <Loading />}
      {query.isError && (
        <div className="alert alert-danger fs-085">
          {getApiErrorMessage(query.error, "Could not load the branch tree.")}
        </div>
      )}
      {query.data && (
        <>
          {query.data.unassignedEmployees > 0 && (
            <div className="alert alert-warning fs-080 py-2">
              <i className="bi bi-exclamation-triangle me-1"></i>
              {query.data.unassignedEmployees} employee(s) have no branch.
            </div>
          )}
          {nodes.length === 0 ? (
            <p className="fs-085 text-slate-500 mb-0">No branches yet.</p>
          ) : (
            <HierarchyChart
              label="Branch tree"
              nodes={nodes}
              renderCard={card}
              renderRow={row}
              isInactive={(n) => n.isActive === false}
              searchText={(n) =>
                `${n.branchName} ${n.branchCode || ""} ${n.manager?.name || ""}`
              }
              onOpen={onOpen}
              view={view}
            />
          )}
        </>
      )}
    </>
  );
};

/** BranchChart in a dialog (company drawer). */
export const BranchTreeDialog = ({ companyId, onClose, onEdit }) => (
  <Dialog
    title="Branch tree"
    subtitle={
      onEdit
        ? "Headcount: own / including sub-branches · click a branch to edit it"
        : "Headcount: own / including sub-branches"
    }
    size="xl"
    onClose={onClose}
  >
    <BranchChart companyId={companyId} onOpen={onEdit} />
  </Dialog>
);

const holderLine = (n) => {
  const holders = n.holders || EMPTY;
  const total = n.holderCount ?? holders.length;
  if (!total) return null;
  const shown = holders
    .slice(0, 3)
    .map((h) => h.name)
    .join(", ");
  return total > 3 ? `${shown} +${total - Math.min(3, holders.length)}` : shown;
};

/** Role hierarchy (reportsTo) as an org chart: holders, headcount, shared roles. */
export const RoleTreeDialog = ({ companyId, onClose }) => {
  const query = useRoleTree(companyId);
  const nodes = query.data?.data || EMPTY;
  const card = (n) => {
    const names = holderLine(n);
    return (
      <>
        <div className="hc-card-title">{n.title}</div>
        <div className="hc-card-badges">
          {n.shared && (
            <span className="status-pill status-pill--blue">Shared</span>
          )}
          {n.isActive === false && (
            <span className="status-pill status-pill--gray">Inactive</span>
          )}
        </div>
        {names ? (
          <div
            className="hc-card-sub mt-1"
            title={(n.holders || EMPTY).map((h) => h.name).join(", ")}
          >
            <i className="bi bi-people me-1"></i>
            {n.holderCount} · {names}
          </div>
        ) : (
          <div className="hc-card-sub mt-1 hc-warn">
            <i className="bi bi-exclamation-circle me-1"></i>Vacant
          </div>
        )}
      </>
    );
  };
  const row = (n) => (
    <>
      <span className="org-name">{n.title}</span>
      {n.shared && (
        <span className="status-pill status-pill--blue">Shared</span>
      )}
      {n.isActive === false && (
        <span className="status-pill status-pill--gray">Inactive</span>
      )}
      <span className="org-meta">
        <i className="bi bi-people me-1"></i>
        {n.holderCount ?? 0}
      </span>
      <span
        className={`org-meta text-truncate ${holderLine(n) ? "" : "hc-warn"}`}
      >
        {holderLine(n) || "Vacant"}
      </span>
    </>
  );

  return (
    <Dialog
      title="Role hierarchy"
      subtitle="Each role under the role it reports to"
      size="xl"
      onClose={onClose}
    >
      {query.isLoading && <Loading />}
      {query.isError && (
        <div className="alert alert-danger fs-085">
          {getApiErrorMessage(
            query.error,
            "Could not load the role hierarchy.",
          )}
        </div>
      )}
      {query.data &&
        (nodes.length === 0 ? (
          <p className="fs-085 text-slate-500 mb-0">No roles yet.</p>
        ) : (
          <HierarchyChart
            label="Role hierarchy"
            nodes={nodes}
            renderCard={card}
            renderRow={row}
            isInactive={(n) => n.isActive === false}
            searchText={(n) =>
              `${n.title} ${(n.holders || EMPTY).map((h) => h.name).join(" ")}`
            }
          />
        ))}
    </Dialog>
  );
};
